import { FC, ChangeEvent, useState, useMemo } from 'react'
import { AboutAttorneyFormContainer } from 'src/components/application/defaultComponents'
import { LeftBar } from 'src/components/basic/LeftBar'
import StandardInput from 'src/components/basic/StandardInput'
import StandardSelect from 'src/components/basic/StandardSelect'
import StepBullet from 'src/components/basic/StepBullet'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'
import { aboutAttorneyPosition, aboutAttorneyPrivatePractice } from 'src/constants/application'
import { object, string } from 'yup'
import { AttorneyData } from 'src/dtos/applicationData'

import { validEmailPattern } from 'src/constants/general'
import StandardFormatInput from 'src/components/basic/StandardFormatInput'
import { isInValidHours, validateDateField } from 'src/utils/formDataManipulation'


interface Props {
  attorney: AttorneyData;
  setAttorneyInfo: (changes: Partial<AttorneyData>) => void;
  handleSaveDetails: (section: string) => Promise<void>;
  status: {
    isAccordion: boolean;
    isOpen: boolean;
  }
  isIncorrect?: boolean;
  toggleOpenAccordion?: () => void;
  errorCodes?: string[];
}


const About: FC<Props> = ({
  attorney,
  setAttorneyInfo,
  isIncorrect,
  handleSaveDetails,
  status,
  toggleOpenAccordion,
  errorCodes = [],
}) => {
  const [emailInteractionCount, setEmailInteractionCount] = useState<number>(0);
  const [joinedDateHasBlurred, setJoinedDateHasBlurred] = useState(false);
  const [averageHoursHasBlurred, setAverageHoursHasBlurred] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setAttorneyInfo({ [name]: value });
  };
  const handleWorkHistoryChange = (changes: Partial<AttorneyData['workHistory']>) => {
    setAttorneyInfo({ workHistory: { ...attorney.workHistory, ...changes } });
  };

  const formContainerClasses = `form-container `

  const validationSchema = object({
    barCardNumber: string().trim().matches(/^\d{6}$|^\d{8}$/).required('aboutSection'),
    position: string().required('aboutSection'),
    emailAddress: string().matches(validEmailPattern).required('aboutSection'),
    yearsInPrivatePractice: string().required('aboutSection'),
  })

  const  [isEmailValid, setIsEmailValid] = useState(false)

  const isFormValid = useMemo(() => {

    try {
      validationSchema.validateSync(attorney)
      return true
    } catch(e ) {
      return false
    }
  }, [attorney, validationSchema])

  const isInputIncorrect = () => {
    return isIncorrect && !isFormValid
  }

  const handleBlur = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>)=> {
    setEmailInteractionCount(emailInteractionCount+1);
    let isValid = validEmailPattern.test(event.target.value);
    setIsEmailValid(isValid)
  }

  const handleJoinDateBlur = () => {
    setJoinedDateHasBlurred(true);
  };

  const handleAverageHourDateBlur = () => {
    setAverageHoursHasBlurred(true);
  }

  const isJoinedDateIncorrect = () => {
    // Postpone judgement that the input is incorrect until after either:
    // 1) a blur occurs
    //   or:
    // 2) when the whole form is validated (ex: Save & continue)
    // Until either of those, return false (meaning the input is correct)
    if (!joinedDateHasBlurred) {
      return false
    }

    //return false
    if (joinedDateHasBlurred && attorney?.workHistory?.joinedDate === '') {
      return true
    }

    //1) blur test
    if (joinedDateHasBlurred
      && validateDateField(attorney?.workHistory?.joinedDate, false, 10, true).length > 0) {
      return true;
    }
    //2) the standard whole form check
    return isInputIncorrect();
  }

  const errMsg = joinedDateHasBlurred ? validateDateField(attorney?.workHistory?.joinedDate, false, 10, true) : '';
  const isIncorrecthoursPerWeek = averageHoursHasBlurred ? isInValidHours(attorney?.workHistory?.hoursPerWeek) : false;

  return (
    <AboutAttorneyFormContainer>
      <LeftBar />
      <div className={formContainerClasses}>
        <div className='form-title'>
          <StepBullet isComplete>1</StepBullet>
          <h6>ROLE</h6>
          <button onClick={toggleOpenAccordion}>
            {status.isOpen ? (
              <MdKeyboardArrowUp />
            ) : (
              <MdKeyboardArrowDown />
            )}
          </button>
        </div>
        <div className='form-content'>
          <StandardFormatInput
            label='Date joined present firm'
            maskStyle='date'
            leftIcon='calendar'
            value={attorney?.workHistory?.joinedDate ?? ''}
            onValueChange={({ formattedValue }) => handleWorkHistoryChange({ joinedDate: formattedValue })}
            onBlur={handleJoinDateBlur}
            isIncorrect={isJoinedDateIncorrect()}
            errorMessage={errMsg}
            errorCode={errorCodes[12]}
          />
          <StandardSelect
            label='Position'
            name="position"
            value={attorney?.position ?? ''}
            onChange={handleChange}
            isIncorrect={isInputIncorrect() && !attorney?.position}
            errorMessage={isInputIncorrect() && !attorney?.position ? 'This is a required field' : ''}
            errorCode={errorCodes[3]}
          >
            <option disabled value="">Select</option>
            {aboutAttorneyPosition.map((position, index) => (
              <option key={index} value={position}>{position}</option>
            ))}
          </StandardSelect>
          <StandardInput
            label={<span>During the next year, how many average hours per week does this attorney anticipate working for <b><u>this</u></b> firm (<u>not</u> just billable)? Includes things such as administrative work, management, marketing, etc.</span>}
            type="number"
            value={String(attorney?.workHistory?.hoursPerWeek ?? '')}
            onChange={({ target: { value } }) => handleWorkHistoryChange({ hoursPerWeek: (isNaN(Number(value)) || !value) ? undefined : Number(value) })}
            onBlur={handleAverageHourDateBlur}
            isIncorrect={isIncorrecthoursPerWeek}
            errorMessage={isIncorrecthoursPerWeek ? 'Must be between 0 and 99' : ''}
            min={0}
            max={99}
            errorCode={errorCodes[13]}
          />
          <StandardInput
            label='Email'
            type='email'
            name="emailAddress"
            value={attorney?.emailAddress ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            isIncorrect={isInputIncorrect() && !attorney?.emailAddress && isEmailValid}
            errorMessage={!isEmailValid && emailInteractionCount > 0 ? 'Please input a valid email address' : ''}//{isInputIncorrect() && !attorney ?.emailAddress && isEmailValid ? 'This is a required field' : ''}
            errorCode={errorCodes[4]}
          />
          <StandardSelect
            label='How many total years of private practice experience does the attorney have from the time they were licensed to present?'
            labelDescription='Private Practice means: Working as a sole practitioner or for a law firm which is independent of any corporate or governmental control or ownership.'
            name="yearsInPrivatePractice"
            value={attorney?.yearsInPrivatePractice ?? ''}
            onChange={handleChange}
            isIncorrect={isInputIncorrect() && !attorney?.yearsInPrivatePractice}
            errorMessage={isInputIncorrect() && !attorney?.yearsInPrivatePractice ? 'This is a required field' : ''}
            errorCode={errorCodes[5]}
          >
            <option disabled value="">Select</option>
            {aboutAttorneyPrivatePractice.map((practice, index) => (
              <option key={index} value={index}>{practice}</option>
            ))}
          </StandardSelect>
          {/* <Button
            onClick={() => onSaveDetailsClick()}
            disabled={!isFormValid}
            isTakingAction={disabledSaveDetailsButton}
          >
            {disabledSaveDetailsButton ? <img src={loadingImg} alt="loading" /> : 'Continue'}
          </Button> */}
        </div>
      </div>
    </AboutAttorneyFormContainer>
  )
}

export default About